import { useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { defineAbilityFor } from "src/acl/ability";
import { useAuth } from "src/contexts/AuthContext";
import { permissionActions, permissionKeys, routePaths } from "src/utils/constants";

type ProtectedRouteProps = React.PropsWithChildren & {
   requiredPermissions: string[]
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermissions }) => {
   const { user, isLoading, signIn } = useAuth();
   const location = useLocation();

   const ability = useMemo(() => defineAbilityFor(user), [user]);

   useEffect(() => {
      if (!user && !isLoading) {
         sessionStorage.setItem('postLoginRedirect', location.pathname + location.search);
         signIn({ handoff: { trigger: 'protected_route' } });
      }
   }, [user, isLoading, signIn, location]);

   // While loading show nothing
   if (!user || isLoading) {
      return <></>;
   }

   // Navigate to unauthorized page
   for (const permission of requiredPermissions) {
      if (permission === permissionKeys.PLATFORM_ADMIN) {
         const can = ability.can(permissionActions.MANAGE, 'all');
         if (!can) {
            return <Navigate to={routePaths.UNAUTHORIZED} replace />;
         }
      } else {
         const action = permission.split('_')[0].toLowerCase()
         const subject = permission.split('_')[1].toLowerCase()

         if (!ability.can(action, subject)) {
            return <Navigate to={routePaths.UNAUTHORIZED} replace />;
         }
      }
   }

   return <>{children}</>;
};

export default ProtectedRoute;